// screens/marketplace/coursesManageApi.js
//
// Admin-side wrappers for course management.
//
//   POST   /marketplace/courses/                       → create
//   PATCH  /marketplace/courses/{id}/                  → update
//   DELETE /marketplace/courses/{id}/                  → deactivate
//   GET    /marketplace/courses/admin/enrollments/     → all enrollments
//   POST   /marketplace/courses/pricing/               → price override
//   PATCH  /marketplace/courses/pricing/{id}/          → update override
//   DELETE /marketplace/courses/pricing/{id}/          → remove override
//   GET    /marketplace/courses/pricing/               → list overrides

const readApiError = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const parts = [];
    for (const [key, val] of Object.entries(errs)) {
      const arr = Array.isArray(val) ? val : [val];
      arr.forEach((msg) => {
        if (key === "non_field_errors" || key === "detail") parts.push(msg);
        else parts.push(`${key.replace(/_/g, " ")}: ${msg}`);
      });
    }
    if (parts.length) return parts.join(" ");
  }
  return fallback;
};

/* ══════════════════════════════════════════════════════════
   Course CRUD
   ══════════════════════════════════════════════════════════ */

export const createCourse = async (authFetch, payload) => {
  const body = toFormData(payload);
  const res = await authFetch("/marketplace/courses/", {
    method: "POST",
    body,
    // Leave Content-Type unset so the browser adds the multipart boundary.
  });
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not create course"));
  return data.data;
};

export const updateCourse = async (authFetch, id, payload) => {
  const body = toFormData(payload);
  const res = await authFetch(`/marketplace/courses/${id}/`, {
    method: "PATCH",
    body,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not update course"));
  return data.data;
};

export const deactivateCourse = async (authFetch, id) => {
  const res = await authFetch(`/marketplace/courses/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(readApiError(data, "Could not deactivate course"));
  }
  return true;
};

/* ══════════════════════════════════════════════════════════
   Enrollments
   ══════════════════════════════════════════════════════════ */

export const listAdminEnrollments = async (
  authFetch,
  { status, course } = {}
) => {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (course) qs.set("course", course);
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await authFetch(
    `/marketplace/courses/admin/enrollments/${suffix}`
  );
  const data = await res.json();
  if (!res.ok)
    throw new Error(readApiError(data, "Could not load enrollments"));
  return data.data || [];
};

/* ══════════════════════════════════════════════════════════
   Pricing overrides
   ══════════════════════════════════════════════════════════ */

export const listCoursePricing = async (authFetch, filters = {}) => {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await authFetch(`/marketplace/courses/pricing/${suffix}`);
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not load pricing"));
  return data.data || [];
};

export const saveCoursePricing = async (authFetch, payload) => {
  const res = await authFetch("/marketplace/courses/pricing/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(readApiError(data, "Could not save price"));
  return data.data;
};

export const deleteCoursePricing = async (authFetch, id) => {
  const res = await authFetch(`/marketplace/courses/pricing/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(readApiError(data, "Could not remove price"));
  }
  return true;
};

/* ══════════════════════════════════════════════════════════
   Price type metadata — mirrors the product-side matrix
   ══════════════════════════════════════════════════════════ */

export const COURSE_PRICE_TYPES = {
  ADMIN_AGENT_CUSTOM: {
    value: "ADMIN_AGENT_CUSTOM",
    label: "Agent cost (admin override)",
    scope: "agent-cost",
    setBy: ["ADMIN"],
  },
  AGENT_CUSTOMER_PRICE: {
    value: "AGENT_CUSTOMER_PRICE",
    label: "Customer price",
    scope: "customer-facing",
    setBy: ["ADMIN", "AGENT"],
  },
  AGENT_SUB_BASE_PRICE: {
    value: "AGENT_SUB_BASE_PRICE",
    label: "Sub-agent base cost",
    scope: "sub-agent-cost",
    setBy: ["ADMIN", "AGENT"],
  },
  AGENT_SUB_CUSTOM_PRICE: {
    value: "AGENT_SUB_CUSTOM_PRICE",
    label: "Sub-agent cost (individual)",
    scope: "sub-agent-cost-override",
    setBy: ["ADMIN", "AGENT"],
  },
  SUB_AGENT_CUSTOMER_PRICE: {
    value: "SUB_AGENT_CUSTOMER_PRICE",
    label: "Customer price",
    scope: "customer-facing",
    setBy: ["ADMIN", "SUB_AGENT"],
  },
};

export const coursePriceTypeMeta = (value) =>
  Object.values(COURSE_PRICE_TYPES).find((t) => t.value === value) || {
    value,
    label: value,
    scope: "unknown",
    setBy: [],
  };

export const availableCoursePriceTypes = (role, context) => {
  if (context === "customer") {
    if (role === "ADMIN" || role === "AGENT")
      return [COURSE_PRICE_TYPES.AGENT_CUSTOMER_PRICE];
    if (role === "SUB_AGENT")
      return [COURSE_PRICE_TYPES.SUB_AGENT_CUSTOMER_PRICE];
  }
  if (context === "subAgent" && (role === "ADMIN" || role === "AGENT")) {
    return [
      COURSE_PRICE_TYPES.AGENT_SUB_BASE_PRICE,
      COURSE_PRICE_TYPES.AGENT_SUB_CUSTOM_PRICE,
    ];
  }
  return [];
};

/* ══════════════════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════════════════ */

/**
 * Convert a course payload into FormData.
 * Needed because the create/update endpoints accept multipart for the
 * cover image upload. Only includes fields that are actually present.
 */
const toFormData = (obj) => {
  const fd = new FormData();
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    if (key === "cover_image" && value instanceof File) {
      fd.append("cover_image", value);
    } else {
      fd.append(key, typeof value === "object" ? JSON.stringify(value) : value);
    }
  }
  return fd;
};